import { useEffect, useState } from 'react';
import { adminSupportOrgsApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { StatusPill } from '../../components/StatusPills';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Plus, Pencil, Trash2, PhoneCall, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '../../components/ConfirmDialog';

const CATEGORY_LABEL = {
  care_specific: 'Care-specific',
  general_listening: 'General listening',
  sexual_abuse: 'Sexual abuse',
  domestic_violence: 'Domestic violence',
  mental_health: 'Mental health',
  suicide_selfharm: 'Suicide / self-harm',
  substance_misuse: 'Substance misuse',
  bereavement: 'Bereavement',
  criminal_justice: 'Criminal justice',
  advocacy_legal: 'Advocacy / legal',
  other: 'Other',
};

const AdminSupportOrgs = () => {
  const [orgs, setOrgs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminSupportOrgsApi.list(
        categoryFilter ? { category: categoryFilter } : undefined
      );
      setOrgs(res.data?.results || []);
      setCategories(res.data?.categories || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [categoryFilter]); // eslint-disable-line

  const beginCreate = () => setEditing({ category: 'other' });
  const beginEdit = (o) => setEditing({ ...o });

  const handleSave = async () => {
    if (!editing?.name?.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        description: (editing.description || '').trim() || null,
        phone: (editing.phone || '').trim() || null,
        url: (editing.url || '').trim() || null,
        category: editing.category || 'other',
      };
      if (editing.id) {
        await adminSupportOrgsApi.update(editing.id, payload);
        toast.success('Organisation updated');
      } else {
        await adminSupportOrgsApi.create(payload);
        toast.success('Organisation added');
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmDialog = useConfirm();
  const handleDelete = async (o) => {
    if (!(await confirmDialog({ title: 'Archive this organisation?', detail: o.name, description: 'It stops appearing on records straight away but is not deleted.', confirmLabel: 'Archive' }))) return;
    try {
      await adminSupportOrgsApi.delete(o.id);
      toast.success('Archived');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-support-orgs-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-foreground">Support organisations</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            The list of helplines and charities surfaced to users on records that are flagged
            as sensitive. Enable emotional safety on a Document Type or Library Tag and
            select which of these orgs to suggest.
          </p>
        </div>
        <Button onClick={beginCreate} data-testid="support-orgs-new-btn">
          <Plus className="w-4 h-4 mr-1.5" />
          Add organisation
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="w-full sm:w-64">
            <Select value={categoryFilter || 'all'} onValueChange={(v) => setCategoryFilter(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="support-orgs-category-filter">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{CATEGORY_LABEL[c] || c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="hidden md:table-cell">Phone</TableHead>
                <TableHead className="hidden md:table-cell">URL</TableHead>
                <TableHead className="w-[120px]">Status</TableHead>
                <TableHead className="text-right w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Loading…</TableCell></TableRow>
              )}
              {!loading && orgs.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No organisations.</TableCell></TableRow>
              )}
              {orgs.map((o) => (
                <TableRow key={o.id} data-testid={`support-orgs-row-${o.id}`}>
                  <TableCell>
                    <div className="font-medium">{o.name}</div>
                    {o.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 max-w-md truncate">{o.description}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-xs">
                      {CATEGORY_LABEL[o.category] || o.category}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm">
                    {o.phone ? (
                      <a href={`tel:${o.phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-1 text-primary hover:underline">
                        <PhoneCall className="w-3 h-3" /> {o.phone}
                      </a>
                    ) : '—'}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm">
                    {o.url ? (
                      <a href={o.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
                        <ExternalLink className="w-3 h-3" />
                        Open
                      </a>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    {o.active !== false ? (
                      <StatusPill tone="done" size="sm">Active</StatusPill>
                    ) : (
                      <StatusPill tone="neutral" size="sm">Archived</StatusPill>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => beginEdit(o)}
                        data-testid={`support-orgs-edit-${o.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                        onClick={() => handleDelete(o)}
                        data-testid={`support-orgs-delete-${o.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit / create dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="support-orgs-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit organisation' : 'Add organisation'}</DialogTitle>
            <DialogDescription>
              These details appear verbatim on records tagged for emotional safety.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={editing?.name || ''}
                onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))}
                data-testid="support-orgs-edit-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={editing?.category || 'other'}
                onValueChange={(v) => setEditing((p) => ({ ...p, category: v }))}
              >
                <SelectTrigger data-testid="support-orgs-edit-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{CATEGORY_LABEL[c] || c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={editing?.phone || ''}
                  onChange={(e) => setEditing((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="e.g. 0800 023 2033"
                  data-testid="support-orgs-edit-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={editing?.url || ''}
                  onChange={(e) => setEditing((p) => ({ ...p, url: e.target.value }))}
                  placeholder="https://…"
                  data-testid="support-orgs-edit-url"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                rows={3}
                value={editing?.description || ''}
                onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))}
                placeholder="Short description of what they offer"
                data-testid="support-orgs-edit-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="support-orgs-edit-save">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSupportOrgs;
